import React, { useMemo } from 'react';
import cn from 'class-names';

import { SVGIcon } from '@uikit/old/Icon/Icon';
import { GREY_COLOR } from '@constants/colors';

import {
  getImportLanguages,
  getImportVerticals,
  getImportWebsiteTypes,
  IMPORT_COLUMN_RULES,
  IMPORT_MULTI_VALUE_HINT,
  ImportReferenceValue,
} from '../../reference';

import './ImportColumnsReference.scss';

type ImportColumnsReferencePropsType = {
  isOpen: boolean;
  onToggle: () => void;
};

function ValueList({ values }: { values: ImportReferenceValue[] }): JSX.Element {
  return (
    <ul className="import-columns-reference__values">
      {values.map(({ code, label }) => (
        <li key={code} className="import-columns-reference__value" title={label}>
          <code className="import-columns-reference__code">{code}</code>
          <span className="import-columns-reference__value-label">{label}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Collapsible cheat sheet of the template: every column with its rule, and every value the
 * backend accepts for languages, verticals and website types. The per-niche `price_<niche>`
 * columns are deliberately not listed: the template already carries them (RES-7302).
 */
function ImportColumnsReference({
  isOpen,
  onToggle,
}: ImportColumnsReferencePropsType): JSX.Element {
  const sections = useMemo(
    () => [
      { title: 'Languages', column: 'languages', values: getImportLanguages() },
      { title: 'Verticals', column: 'verticals', values: getImportVerticals() },
      { title: 'Website types', column: 'website_types', values: getImportWebsiteTypes() },
    ],
    []
  );

  return (
    <div className={cn('import-columns-reference', { 'import-columns-reference--open': isOpen })}>
      <button type="button" className="import-columns-reference__toggle" onClick={onToggle}>
        <span>{isOpen ? 'Hide' : 'Show'} columns and accepted values</span>
        <SVGIcon icon="chevron" color={GREY_COLOR} size={10} />
      </button>

      {isOpen ? (
        <div className="import-columns-reference__content">
          <p className="import-columns-reference__hint">{IMPORT_MULTI_VALUE_HINT}</p>

          <table className="import-columns-reference__rules">
            <tbody>
              {IMPORT_COLUMN_RULES.map(({ column, rule }) => (
                <tr key={column}>
                  <td className="import-columns-reference__rules-column">
                    <code className="import-columns-reference__code">{column}</code>
                  </td>
                  <td className="import-columns-reference__rules-rule">{rule}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {sections.map(({ title, column, values }) => (
            <div key={column} className="import-columns-reference__section">
              <div className="import-columns-reference__section-title">
                {title}
                <code className="import-columns-reference__code">{column}</code>
              </div>
              <ValueList values={values} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default ImportColumnsReference;
